import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import Page from "@components/Page.vue";
import CyberRange from "@components/guacamole/CyberRange.vue";
import Config from './config';
import { resolve } from 'url';
import Route from '@/interfaces/route';
import { IPlayer, PlayerStatus } from '@cyber-range/cyber-range-api-ctf-player-client';
import { Competition, ICompetition } from '@cyber-range/cyber-range-api-ctf-competition-client';
import { useSplashScreenStore } from '@/stores/splashScreenStore';
import { useEntitlementStore } from '@stores/entitlementStore';
import { useAuthenticationStore } from '@stores/authenticationStore';
import { useCompetitionStore } from '@stores/competitionStore';
import { useAuthorizationStore } from '@stores/authorizationStore';
import { usePlayerStore } from './stores/playerStore';
import { useTeamStore } from './stores/teamStore';
import { useKnowledgeStore } from './stores/knowledgeStore';
import { useLibraryEntryStore } from './stores/libraryEntryStore';
import { useNotificationStore } from './stores/notificationStore';
import { isThemeEditorPreview } from './themeEditor/isThemeEditorPreview';

const competitionSubdomainParamKey = 'competitionSubdomain';

async function injectSubdomainAsRouteParam(to) {
    to.params[competitionSubdomainParamKey] = location.hostname.split('.')[0];
}

async function allowSso(to)
{
    if(to.query['s']){
        await useAuthenticationStore().login({provider: 'sso', token: to.query['s']});
        delete to.query['s'];
        return {path: to.path, replace: true};
    }
    else if(to.query['t']){
        await useAuthenticationStore().login({ token: to.query['t'], provider: 'vcr'});
        delete to.query['t'];
        return {path: to.path, replace: true};
    }

    return;
}

async function login(): Promise<boolean>
{
    const authenticationStore = useAuthenticationStore();
    if (authenticationStore.isLogin || (await authenticationStore.tryLogin()))
    {
        if(!useEntitlementStore().claimsFetched)
        {
            await useEntitlementStore().fetchClaims();
            //After logged in, refetch the competition to get the competition's private information (i.e. categories).
            await useCompetitionStore().refreshCurrentCompetition();
        }
        return true;
    }
    else
    {
        return false;
    }
}

async function requireAuth(to)
{
    let isWww = to.params[competitionSubdomainParamKey] === 'www';
    let isThemeEditor = isThemeEditorPreview()

    if(await login())
    {
        //After logged in, refetch the competition to get the competition's private information (i.e. categories).
        if(!isWww && !isThemeEditor)
        {
            //If subdomain changed, refresh the current competition
            let competition = useCompetitionStore().currentCompetition
            if(competition?.subdomain !== to.params[competitionSubdomainParamKey])
            {
                await useCompetitionStore().refreshCurrentCompetition();
            }
        }
    }
    else
    {
        let loginUrl: string;
        if (isWww)
        {
            loginUrl = resolve(`${Config.LOGIN_UI_BASE_URL}`, `?redirect_url=${resolve(Config.CTF_UI_BASE_URL, Route.Competitions.path)}`)
        }
        else if (isThemeEditor)
        {
            loginUrl = resolve(`${Config.LOGIN_UI_BASE_URL}`, `?redirect_url=${Config.CTF_UI_BASE_URL}`)
        }
        else
        {
            loginUrl = resolve(`${Config.LOGIN_UI_BASE_URL}`, `/join/${to.params[competitionSubdomainParamKey]}?redirect_url=${resolve(Config.CTF_UI_BASE_URL, Route.Challenges.path)}`);
        }

        window.location.replace(loginUrl);
    }
}

async function optionallyAuth()
{
    await login();
}

async function requireCanUpdateCompetition() {
    let competition = useCompetitionStore().currentCompetition;
    if (!useAuthorizationStore().canUpdateCompetition(competition.id, competition.organizationId))
    {
        await useAuthenticationStore().logout();
        return Route.Landing;
    }

    return;
}

async function requireWhenCybersafeAI() {
    let competition = new Competition(useCompetitionStore().currentCompetition);
    if(competition.isCybersafeAI())
    {
        return await requireCanUpdateCompetition();
    }

    return;
}

async function requireCompetition()
{
    if (useCompetitionStore().currentCompetition?.name === undefined)
    {
        return Route.Competitions;
    }

    return;
}
async function requireAllowTeamMembershipChange()
{
    return useCompetitionStore().currentCompetition.settings.allowTeamMembershipChange
        ? undefined
        : {...Route.Welcome, hash:"#main"};
}

async function requireCompetitionIsUnset()
{
    if (useCompetitionStore().currentCompetition?.name !== undefined)
    {
        return Route.Landing;
    }

    return;
}

async function scoreboardIsEnabled()
{
    let competition = useCompetitionStore().currentCompetition;

    if(competition.settings?.hideDashboard)
    {
        if (!(useAuthenticationStore().isLogin && useAuthorizationStore().canViewHiddenScoreboard(competition.id, competition.organizationId)))
        {
            return Route.Landing;
        }
    }

    return;
}

async function isInTeamOrCanViewAllCompetitions()
{
    let competition:ICompetition = useCompetitionStore().currentCompetition;

    if (useAuthorizationStore().canViewAllCompetitions(competition.organizationId))
    {
        return;
    }

    return await requireTeam();
}

async function notJoiningATeam()
{
    const playerStore = usePlayerStore()
    let player:IPlayer = playerStore.getCurrentPlayer || await playerStore.fetchCurrentPlayer();

    if (player && player.status === PlayerStatus.Joining)
    {
        return Route.Team;
    }

    return;
}

async function tryFetchTeam()
{
    try
    {
        useTeamStore().getMyTeam || await useTeamStore().fetchMyTeam();
    }
    catch{}
}

async function requireTeam()
{
    try
    {
        let myTeamId = useTeamStore().getMyTeam || await useTeamStore().fetchMyTeam();

        if(myTeamId)
        {
            return;
        }
    }
    catch{}

    return {...Route.Welcome, hash:"#main"};
}

async function requireNoTeam()
{
    let competition:ICompetition = useCompetitionStore().currentCompetition;

    if (useAuthorizationStore().canViewAllCompetitions(competition.organizationId))
    {
        return Route.Challenges;
    }

    const playerStore = usePlayerStore()
    let player:IPlayer = playerStore.player || await usePlayerStore().fetchCurrentPlayer();

    if(player?.teamId)
    {
        return Route.Challenges;
    }

    return;
}
/**
 * Redirect /manage to /manage/settings
 */
async function requireManageSubsection(to)
{
    if(to.name === Route.Manage.name)
    {
        return Route.Settings;
    }

    return;
}

function hideSplashScreen()
{
    useSplashScreenStore().hideSplashScreen();
}

async function requireKnowledge()
{
    let competition:ICompetition = useCompetitionStore().currentCompetition;

    if (useAuthorizationStore().canCreateChallenge(competition.id, competition.organizationId))
    {
        await useKnowledgeStore().fetchKnowledges();
    }
}

function requireLibraryPreload()
{
    let competition:ICompetition = useCompetitionStore().currentCompetition;

    if (useAuthorizationStore().canCreateLibraryEntry(competition.id, competition.organizationId))
    {
        useLibraryEntryStore().preloadLibraryEntries();
    }
}

async function requireCanAccessLibrary()
{
    let competition = useCompetitionStore().currentCompetition;
    if (!useAuthorizationStore().canCreateLibraryEntry(competition.id, competition.organizationId))
    {
        return Route.Settings;
    }

    return;
}

function clearNotifications()
{
    useNotificationStore().clearNotifications();
}


const routes: RouteRecordRaw[] = [

    {
        component: Page,
        path: "/",
        beforeEnter: [injectSubdomainAsRouteParam, allowSso, requireCompetition, hideSplashScreen, clearNotifications],
        children: [
            {...Route.Landing}
        ]
    },
    {
        path: "/",
        component: Page,
        beforeEnter: [injectSubdomainAsRouteParam, allowSso, requireAuth, requireCompetition, requireWhenCybersafeAI, isInTeamOrCanViewAllCompetitions, notJoiningATeam, hideSplashScreen, requireKnowledge, requireLibraryPreload, clearNotifications],
        children: [
            {...Route.Challenges,
                children: [
                    {...Route.CategorizedChallenges}
                ]
            }
        ]
    },
    {
        path: "/",
        component: Page,
        beforeEnter: [injectSubdomainAsRouteParam, allowSso, requireAuth, requireCompetition, requireWhenCybersafeAI, requireTeam, hideSplashScreen, clearNotifications],
        children: [
            {...Route.Team}
        ]
    },
    {
        path: "/",
        component: Page,
        beforeEnter: [injectSubdomainAsRouteParam, allowSso, optionallyAuth, requireCompetition, requireWhenCybersafeAI, scoreboardIsEnabled, tryFetchTeam, hideSplashScreen, clearNotifications],
        children: [
            {...Route.Scoreboard}
        ]
    },
    {
        path: "/",
        component: Page,
        beforeEnter: [injectSubdomainAsRouteParam, allowSso, requireAuth, requireCompetition, requireWhenCybersafeAI, requireNoTeam, hideSplashScreen, clearNotifications],
        children: [
            {...Route.Welcome, children: [
                {...Route.JoinTeam, beforeEnter: requireAllowTeamMembershipChange },
                {...Route.CreateTeam, beforeEnter: requireAllowTeamMembershipChange },
            ]}
        ]
    },
    {
        path: "/",
        component: Page,
        beforeEnter: [injectSubdomainAsRouteParam, allowSso, requireAuth, requireCompetition, requireCanUpdateCompetition, requireManageSubsection, hideSplashScreen, requireKnowledge, requireLibraryPreload, clearNotifications],
        children: [
            {...Route.Manage, children: [
                {...Route.Settings, beforeEnter: clearNotifications},
                {...Route.Stats, beforeEnter: clearNotifications},
                {...Route.Players, beforeEnter: clearNotifications},
                {...Route.Teams, beforeEnter: clearNotifications},
                {...Route.ManageChallenges, beforeEnter: clearNotifications},
                {...Route.Library, beforeEnter: [requireCanAccessLibrary, clearNotifications]}
            ]}
        ]
    },
    {
        beforeEnter: [injectSubdomainAsRouteParam, allowSso, requireAuth, hideSplashScreen, clearNotifications],
        component: CyberRange,
        ...Route.ChallengeEnvironment,
        props: true,

    },
    {
        path: "/",
        component: Page,
        beforeEnter: [injectSubdomainAsRouteParam, allowSso, requireAuth, requireCompetitionIsUnset, hideSplashScreen, clearNotifications],
        children: [
            {...Route.Competitions}
        ]
    }

];

let router = createRouter(
{
    history: createWebHistory(),
    routes,
    scrollBehavior: (to, from, savedPosition) => {
        if (savedPosition) {
            return savedPosition;
        } else {
            return { top: 0, left: 0 };
        }
    }
});

export default router;
